/**
 * Subscribes to the backend SSE stream (new book uploads). Uses fetch + streaming
 * so the Authorization header can be sent (unlike EventSource).
 *
 * Deployments: if an API reverse proxy buffers responses, disable buffering and
 * allow long read timeouts for GET /api/books/events so SSE delivers promptly.
 *
 * @param {object} options
 * @param {string} options.url - Full URL, e.g. `${API_BASE_URL}/books/events`
 * @param {string|null} options.token - Bearer token
 * @param {AbortSignal} options.signal
 * @param {(payload: { id: string, title: string }) => void} options.onEvent
 * @param {() => void} [options.onUnauthorized]
 * @param {(err: unknown) => void} [options.onError]
 */
export async function subscribeToNewBookEvents({
  url,
  token,
  signal,
  onEvent,
  onUnauthorized,
  onError,
}) {
  if (!token) {
    return;
  }

  let response;
  try {
    response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "text/event-stream",
        Authorization: `Bearer ${token}`,
      },
      signal,
    });
  } catch (e) {
    if (signal.aborted) {
      return;
    }
    onError?.(e);
    return;
  }

  if (response.status === 401) {
    onUnauthorized?.();
    return;
  }

  if (!response.ok) {
    onError?.(new Error(`SSE failed: ${response.status}`));
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    onError?.(new Error("No response body for SSE"));
    return;
  }

  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      const segments = buffer.split(/\r?\n\r?\n/);
      buffer = segments.pop() ?? "";
      for (const segment of segments) {
        const dataPayload = extractSseData(segment);
        if (dataPayload) {
          try {
            const parsed = JSON.parse(dataPayload);
            if (parsed?.id && parsed?.title != null) {
              onEvent(parsed);
            }
          } catch (e) {
            onError?.(e);
          }
        }
      }
    }
  } catch (e) {
    if (signal.aborted) {
      return;
    }
    onError?.(e);
  } finally {
    reader.releaseLock();
  }
}

function extractSseData(segment) {
  const lines = segment.split(/\r?\n/);
  const dataLines = [];
  for (const line of lines) {
    if (line.startsWith(":")) {
      continue;
    }
    if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trimStart());
    }
  }
  if (dataLines.length === 0) {
    return null;
  }
  return dataLines.join("\n");
}
